import { useNavigation } from '@react-navigation/native';
import { Headline3, Meta, theme } from 'aimo-ui';
import Icon from 'components/icons/Icon';
import { groupBy } from 'lodash';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, Text } from 'react-native';
import { useMapStore } from 'stores/mapStore';
import styled from 'styled-components/native';
import { CountryCode, ParkZone } from 'types/generatedSchemaTypes';
import { ParkNavProps } from 'types/navigation';
import { getDistanceText } from 'utils/commonUtils';
import { getDistanceFromLatLonInMeters } from 'utils/mapUtils';
import { isValidNote } from 'utils/parkUtils';
import { testIdentifiers } from 'utils/testIdentifiers';
import { getScheduleFromDate } from 'utils/timeUtils';
import AddressAndLocation from './AddressAndLocation';
import { Row } from './styles/styles';

type ParkingZoneDetailsHeadlineProps = {
  parkZone: ParkZone;
  markerCoordinate?: {
    latitude: number;
    longitude: number;
  } | null;
  showSeeMoreLink?: boolean;
  showDistance?: boolean;
};

const StyledMeta = styled(Meta)`
  font-weight: 700;
`;

const ParkingZoneDetailsHeadline: React.FC<ParkingZoneDetailsHeadlineProps> = ({
  parkZone,
  markerCoordinate,
  showSeeMoreLink = true,
  showDistance = false,
}) => {
  const { t } = useTranslation();
  const { navigate: navigatePark } = useNavigation<ParkNavProps>();
  const {
    commercialZoneView,
    name,
    address,
    countryCode,
    uid,
    parkingFacility,
    notes,
  } = parkZone || {};
  let distance: string | undefined;
  const currentLocation = useMapStore((state) => state.userLocation);
  if (currentLocation && markerCoordinate) {
    const distanceNumber = getDistanceFromLatLonInMeters(
      currentLocation,
      markerCoordinate
    );
    distance = showDistance
      ? getDistanceText(distanceNumber / 1000, countryCode as CountryCode)
      : '';
  }

  const { timeSlots: schedule } = parkingFacility || {};
  const groupedSchedule = groupBy(schedule, 'weekDay');
  const { isOpen } = getScheduleFromDate(
    groupedSchedule,
    new Date(),
    countryCode as CountryCode
  );
  const hasAddress = useMemo(
    () => !!address?.street || !!address?.number || !!address?.city,
    [address]
  );

  const handleSeeMore = () => {
    navigatePark('parkInfo', { parkingZoneUid: uid });
  };

  const importantNotes = useMemo(
    () =>
      notes?.filter((note) => isValidNote(note, countryCode, 3)).length ?? 0,
    [notes]
  );

  return (
    <>
      <Row mb={0} mt={0}>
        <Text
          numberOfLines={2}
          {...testIdentifiers('parkzone-details-headline-main')}>
          <Headline3
            color={theme.colors.primary}
            testID="parkzone-details-commercial-zone">
            {commercialZoneView?.zoneCode
              ? `${commercialZoneView?.zoneCode} `
              : ''}
          </Headline3>
          <Headline3 testID="parkzone-details-name">
            {commercialZoneView?.name || name}
          </Headline3>
        </Text>
      </Row>
      <Row mb={0} mt={2} wrap>
        <AddressAndLocation
          color={theme.colors.gray300}
          address={address}
          name={commercialZoneView?.name || name || ''}
          locationText={distance}
          numberOfLines={2}
          addressAsLink={false}
          testID="parkzone-details-type-address"
        />
        {(hasAddress || distance) && (
          <Meta color={theme.colors.gray100}>{' • '}</Meta>
        )}
        <Meta color={theme.colors.gray300} testID="parkzone-details-status">
          {isOpen ? t('parkingZone.open') : t('parkingZone.closed')}
        </Meta>
        {showSeeMoreLink && (
          <>
            <Meta color={theme.colors.gray100}>{' • '}</Meta>
            <Pressable
              onPress={handleSeeMore}
              style={({ pressed }: any) => [
                {
                  opacity: pressed ? 0.5 : 1,
                  flexDirection: 'row',
                  alignItems: 'center',
                },
              ]}>
              <StyledMeta color={theme.colors.boost.green}>
                {t('parkingZone.seeMore')}
              </StyledMeta>
              {importantNotes > 0 && (
                <Icon
                  name="Info-Filled-Black"
                  size="m"
                  color={theme.colors.boost.yellow}
                />
              )}
            </Pressable>
          </>
        )}
      </Row>
    </>
  );
};

export default ParkingZoneDetailsHeadline;
